/**
 * Minimal web implementation of React Native's PanResponder.create() API, backed
 * by Pointer Events. Replaces the abandoned `react-panresponder-web` package,
 * which depended on `react-dom/unstable-native-dependencies` (removed in React 18+).
 *
 * Only the subset consumed by the shared SideMenu is implemented: the gesture
 * lifecycle (grant → move → release) and a gestureState carrying the fields that
 * component reads — dx/dy (accumulated since grant), vx/vy (px per ms), moveX/moveY,
 * x0/y0. Mobile keeps using RN's real PanResponder; this only runs on web.
 */
const PanResponder = {
  create(config = {}) {
    let tracking = false; // pointer is down
    let granted = false; // gesture accepted as a pan
    let x0 = 0;
    let y0 = 0;
    let lastX = 0;
    let lastY = 0;
    let lastT = 0;
    let dx = 0;
    let dy = 0;
    let vx = 0;
    let vy = 0;

    const gestureState = () => ({ dx, dy, vx, vy, moveX: lastX, moveY: lastY, x0, y0, numberActiveTouches: 1 });

    const onPointerDown = e => {
      tracking = true;
      granted = false;
      x0 = lastX = e.clientX;
      y0 = lastY = e.clientY;
      lastT = e.timeStamp;
      dx = dy = vx = vy = 0;
      config.onStartShouldSetPanResponder?.(e, gestureState());
    };

    const onPointerMove = e => {
      if (!tracking) return;

      const dt = e.timeStamp - lastT || 16;
      vx = (e.clientX - lastX) / dt;
      vy = (e.clientY - lastY) / dt;
      dx = e.clientX - x0;
      dy = e.clientY - y0;
      lastX = e.clientX;
      lastY = e.clientY;
      lastT = e.timeStamp;

      if (!granted) {
        if (!config.onMoveShouldSetPanResponder?.(e, gestureState())) return;
        granted = true;
        // Re-anchor so dx/dy are measured from the grant point (RN zeroes them here).
        x0 = e.clientX;
        y0 = e.clientY;
        dx = dy = 0;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        config.onPanResponderGrant?.(e, gestureState());
        return;
      }

      config.onPanResponderMove?.(e, gestureState());
    };

    const onPointerEnd = e => {
      if (granted) config.onPanResponderRelease?.(e, gestureState());
      tracking = false;
      granted = false;
    };

    return {
      panHandlers: {
        onPointerDown,
        onPointerMove,
        onPointerUp: onPointerEnd,
        onPointerCancel: onPointerEnd,
      },
    };
  },
};

export default PanResponder;
